import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { DS } from '../PatientForms/designTokens';
import { FollowupFormData, HeadacheDaysAnswer } from './types';
import { FormRadioGroup } from './FormRadioGroup';
import FormTextInput from './FormTextInput';
import FormSubmittedView from './FormSubmittedView';
import FormLayout from './FormLayout';
import {
  getMyDiarySummary,
  getMyFollowUp,
  getMyNewVisit,
  saveMyFollowUp,
} from './patientFormApi';
import { useAutoSave } from './useAutoSave';
import {
  asksPregnancyQuestions,
  followUpTopLevelFields,
  isValidPregnancyWeek,
} from './formValidation';
import { FOLLOW_UP_FORM_VERSION, useFormVersionTag } from './formVersion';
import HeadacheDaysQuestion from './HeadacheDaysQuestion';
import { DiaryState, diaryForAnswer } from './headacheDaysAnswer';
import { useUnansweredGate } from './useUnansweredGate';
import UnansweredQuestions, {
  UNANSWERED_QUESTIONS_ID,
} from './UnansweredQuestions';
import { reportError } from '../../lib/sentry';

function formDataToSave(formData: FollowupFormData): Record<string, unknown> {
  if (formData.headacheDaysLast3Months?.headacheDaysTotal === undefined) {
    return { ...formData, formVersion: FOLLOW_UP_FORM_VERSION };
  }
  const { estimatedHeadacheDaysLast3Months, ...rest } = formData;
  return { ...rest, formVersion: FOLLOW_UP_FORM_VERSION };
}

const FollowUpForm: React.FC = () => {
  const [formData, setFormData] = useState<FollowupFormData>({
    headacheLogged: undefined,
    missingDiaryInfo: '',
    headacheAssessment: undefined,
  });

  const midasOptions: { label: string; value: number }[] = Array.from(
    { length: 21 },
    (_, i) => ({
      label: i.toString(),
      value: i,
    })
  ).concat({ label: '21 eller fler', value: 21 });

  const hitOptions = [
    { label: 'Aldrig', value: 6 },
    { label: 'Sällan', value: 8 },
    { label: 'Ibland', value: 10 },
    { label: 'Ofta', value: 11 },
    { label: 'Alltid', value: 13 },
  ];

  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [asksPregnancy, setAsksPregnancy] = useState<boolean | undefined>(
    undefined
  );

  useFormVersionTag('followUp', FOLLOW_UP_FORM_VERSION);

  const [error, setError] = useState<string | undefined>(undefined);
  const errorRef = useRef<HTMLParagraphElement>(null);

  const [diary, setDiary] = useState<DiaryState>('loading');

  const fetchDiarySummary = async (saved?: HeadacheDaysAnswer) => {
    try {
      setDiary(
        await diaryForAnswer(saved, async (windowEnd) => {
          const result = await getMyDiarySummary(windowEnd);
          return result.summary;
        })
      );
    } catch (err) {
      reportError(err);
      setDiary('failed');
    }
  };

  const fetchFollowUpForm = async () => {
    try {
      const result = await getMyFollowUp();
      const form = result?.form;
      if (form?.formData) {
        setFormData({ ...form.formData });
      }
      if (form?.status === 'submitted') {
        setFormSubmitted(true);
      }
      setLoading(false);
      return form?.formData?.headacheDaysLast3Months;
    } catch (err) {
      reportError(err);
      setLoadError(true);
      setLoading(false);
      return undefined;
    }
  };

  const fetchBiologicalSex = async () => {
    try {
      const result = await getMyNewVisit();
      setAsksPregnancy(
        asksPregnancyQuestions(
          result?.form?.formQuestionnaire?.data?.biologicalSex
        )
      );
    } catch (err) {
      reportError(err, {
        tags: { context: 'follow_up_biological_sex_lookup' },
      });
      setAsksPregnancy(true);
    }
  };

  useEffect(() => {
    fetchFollowUpForm().then(fetchDiarySummary);
    fetchBiologicalSex();
  }, []);

  const setHeadacheDays = useCallback(
    (answer?: HeadacheDaysAnswer) =>
      setFormData((prev) => ({ ...prev, headacheDaysLast3Months: answer })),
    []
  );

  const autoSaveFn = useCallback(async () => {
    await saveMyFollowUp(formDataToSave(formData), 'inProgress');
  }, [formData]);

  const { trigger: handleAutoSave, cancel: cancelAutoSave } =
    useAutoSave(autoSaveFn);

  const gateFields = useMemo(
    () => ({
      ...formData,
      headacheDaysLast3Months:
        formData.headacheDaysLast3Months?.headacheDaysTotal,
    }),
    [formData]
  );

  const topLevelFields = useMemo(
    () => followUpTopLevelFields(Boolean(asksPregnancy), formData.pregnant),
    [asksPregnancy, formData.pregnant]
  );

  const { unanswered, showUnanswered, incompleteRef, blockIfIncomplete } =
    useUnansweredGate(gateFields, topLevelFields);
  const submitDisabled = isSubmitting || !confirmChecked;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitter = (e.nativeEvent as SubmitEvent)
      ?.submitter as HTMLButtonElement | null;
    const isFinalSubmit = submitter?.name === 'SaveForm';

    if (blockIfIncomplete(isFinalSubmit)) return;

    cancelAutoSave();
    setIsSubmitting(true);
    setError(undefined);
    try {
      const status: 'inProgress' | 'submitted' = isFinalSubmit
        ? 'submitted'
        : 'inProgress';
      await saveMyFollowUp(formDataToSave(formData), status);
      if (isFinalSubmit) setFormSubmitted(true);
    } catch (err) {
      reportError(err);
      setError('Det gick inte att skicka formuläret. Försök igen.');
      setTimeout(
        () =>
          errorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        0
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return <FormSubmittedView />;
  }

  if (loadError) {
    return (
      <FormLayout title="Återbesök Läkare" loading={false}>
        <Typography sx={{ color: DS.inkSecondary, textAlign: 'center', py: 4 }}>
          Formuläret kunde inte laddas. Försök igen senare.
        </Typography>
      </FormLayout>
    );
  }

  return (
    <form noValidate onSubmit={handleSubmit}>
      <FormLayout
        title="Återbesök Läkare"
        description="Det här formuläret hjälper läkaren att följa upp din huvudvärksproblematik sedan förra besöket. Det tar bara några minuter att fylla i. Dina svar sparas automatiskt, och du kan skicka in formuläret när du är klar genom att klicka på knappen längst ner på sidan."
        loading={loading || asksPregnancy === undefined}
      >
        <Box sx={{ mb: 3 }}>
          <FormRadioGroup
            label="Har du registrerat alla dina huvudvärksdagar i dagboken?"
            name="headacheLogged"
            id="q-headacheLogged"
            value={formData.headacheLogged}
            options={[
              { label: 'Ja', value: true },
              { label: 'Nej', value: false },
            ]}
            onChange={(val) =>
              setFormData((p) => ({ ...p, headacheLogged: val }))
            }
            onBlur={handleAutoSave}
          />
          {formData.headacheLogged === false && (
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="Beskriv vad som saknas"
              value={formData.missingDiaryInfo || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  missingDiaryInfo: e.target.value,
                }))
              }
              onBlur={handleAutoSave}
            />
          )}
        </Box>

        <FormRadioGroup
          label="Hur tycker du att din huvudvärk varit sedan föregående besök hos Migränhjälpen?"
          name="headacheAssessment"
          id="q-headacheAssessment"
          value={formData.headacheAssessment}
          options={[
            { value: 'much_better', label: 'Betydligt bättre' },
            { value: 'somewhat_better', label: 'Något bättre' },
            { value: 'unchanged', label: 'Oförändrad' },
            { value: 'somewhat_worse', label: 'Något sämre' },
            { value: 'much_worse', label: 'Betydligt sämre' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              headacheAssessment: val,
            }))
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur uppfattar du antalet huvudvärksdagar sedan sist?"
          name="daysAssessment"
          id="q-daysAssessment"
          value={formData.daysAssessment}
          options={[
            { label: 'Betydligt färre', value: 'much_fewer' },
            { label: 'Något färre', value: 'somewhat_fewer' },
            { label: 'Oförändrat', value: 'unchanged' },
            { label: 'Något fler', value: 'somewhat_more' },
            { label: 'Betydligt fler', value: 'much_more' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              daysAssessment: val as FollowupFormData['daysAssessment'],
            }))
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur uppfattar du smärtintensiteten vid de enskilda anfallen sedan sist?"
          name="painIntensityChange"
          id="q-painIntensityChange"
          value={formData.painIntensityChange}
          options={[
            { value: 'much_less', label: 'Betydligt lindrigare' },
            { value: 'somewhat_less', label: 'Något lindrigare' },
            { value: 'unchanged', label: 'Oförändrad' },
            { value: 'somewhat_more', label: 'Något kraftigare' },
            { value: 'much_more', label: 'Betydligt kraftigare' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              painIntensityChange:
                val as FollowupFormData['painIntensityChange'],
            }))
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur uppfattar du längden på dina anfall sedan sist?"
          name="attackDurationChange"
          id="q-attackDurationChange"
          value={formData.attackDurationChange}
          options={[
            { value: 'much_shorter', label: 'Betydligt kortare' },
            { value: 'somewhat_shorter', label: 'Något kortare' },
            { value: 'unchanged', label: 'Oförändrad' },
            { value: 'somewhat_longer', label: 'Något längre' },
            { value: 'much_longer', label: 'Betydligt längre' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              attackDurationChange:
                val as FollowupFormData['attackDurationChange'],
            }))
          }
          onBlur={handleAutoSave}
        />

        <Box id="q-hasSideEffects" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '14px',
            }}
          >
            Har du haft några biverkningar sedan sist?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontFamily: DS.fontBody,
                fontSize: '14px',
              }}
            >
              <input
                type="radio"
                name="hasSideEffects"
                value="yes"
                checked={formData.hasSideEffects === true}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    hasSideEffects: true,
                  }))
                }
                onBlur={handleAutoSave}
                style={{ marginRight: '8px' }}
              />
              Ja
            </Box>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontFamily: DS.fontBody,
                fontSize: '14px',
              }}
            >
              <input
                type="radio"
                name="hasSideEffects"
                value="no"
                checked={formData.hasSideEffects === false}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    hasSideEffects: false,
                    sideEffectDescription: '',
                    suspectedDrug: '',
                  }))
                }
                onBlur={handleAutoSave}
                style={{ marginRight: '8px' }}
              />
              Nej
            </Box>
          </Box>

          {formData.hasSideEffects === true && (
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <FormTextInput
                labelFontWeight={400}
                labelFontSize="14px"
                label="Vilken biverkan har du haft?"
                value={formData.sideEffectDescription || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sideEffectDescription: e.target.value,
                  }))
                }
                onBlur={handleAutoSave}
              />
              <FormTextInput
                labelFontWeight={400}
                labelFontSize="14px"
                label="Vilket preparat misstänker du har orsakat denna biverkan?"
                value={formData.suspectedDrug || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    suspectedDrug: e.target.value,
                  }))
                }
                onBlur={handleAutoSave}
              />
            </Box>
          )}
        </Box>

        <Box id="q-stoppedMedication" sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '14px',
            }}
          >
            Har du slutat använda någon av dina mediciner?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontFamily: DS.fontBody,
                fontSize: '14px',
              }}
            >
              <input
                type="radio"
                name="stoppedMedication"
                value="yes"
                checked={formData.stoppedMedication === true}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    stoppedMedication: true,
                  }))
                }
                onBlur={handleAutoSave}
                style={{ marginRight: '8px' }}
              />
              Ja
            </Box>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontFamily: DS.fontBody,
                fontSize: '14px',
              }}
            >
              <input
                type="radio"
                name="stoppedMedication"
                value="no"
                checked={formData.stoppedMedication === false}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    stoppedMedication: false,
                    stoppedMedicationName: '',
                    stoppedMedicationDate: '',
                    stoppedMedicationReason: undefined,
                    stoppedMedicationOtherReason: '',
                  }))
                }
                onBlur={handleAutoSave}
                style={{ marginRight: '8px' }}
              />
              Nej
            </Box>
          </Box>

          {formData.stoppedMedication === true && (
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <FormTextInput
                labelFontWeight={400}
                labelFontSize="14px"
                label="Vilken medicin har du slutat med?"
                value={formData.stoppedMedicationName || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    stoppedMedicationName: e.target.value,
                  }))
                }
                onBlur={handleAutoSave}
              />
              <FormTextInput
                labelFontWeight={400}
                labelFontSize="14px"
                label="När slutade du med medicinen?"
                value={formData.stoppedMedicationDate || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    stoppedMedicationDate: e.target.value,
                  }))
                }
                onBlur={handleAutoSave}
              />
              <Box>
                <Typography
                  sx={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#374151',
                    mb: 0.5,
                    fontFamily: DS.fontBody,
                  }}
                >
                  Av vilken anledning slutade du med medicinen?
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Box
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontFamily: DS.fontBody,
                      fontSize: '14px',
                    }}
                  >
                    <input
                      type="radio"
                      name="stoppedMedicationReason"
                      value="side_effects"
                      checked={
                        formData.stoppedMedicationReason === 'side_effects'
                      }
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          stoppedMedicationReason: 'side_effects',
                          stoppedMedicationOtherReason: '',
                        }))
                      }
                      onBlur={handleAutoSave}
                      style={{ marginRight: '8px' }}
                    />
                    Biverkan
                  </Box>
                  <Box
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontFamily: DS.fontBody,
                      fontSize: '14px',
                    }}
                  >
                    <input
                      type="radio"
                      name="stoppedMedicationReason"
                      value="no_effect"
                      checked={formData.stoppedMedicationReason === 'no_effect'}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          stoppedMedicationReason: 'no_effect',
                          stoppedMedicationOtherReason: '',
                        }))
                      }
                      onBlur={handleAutoSave}
                      style={{ marginRight: '8px' }}
                    />
                    Bristande effekt
                  </Box>
                  <Box
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontFamily: DS.fontBody,
                      fontSize: '14px',
                    }}
                  >
                    <input
                      type="radio"
                      name="stoppedMedicationReason"
                      value="other"
                      checked={formData.stoppedMedicationReason === 'other'}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          stoppedMedicationReason: 'other',
                        }))
                      }
                      onBlur={handleAutoSave}
                      style={{ marginRight: '8px' }}
                    />
                    Annat
                  </Box>
                </Box>
              </Box>
              {formData.stoppedMedicationReason === 'other' && (
                <FormTextInput
                  labelFontWeight={400}
                  labelFontSize="14px"
                  label="Ange annat skäl:"
                  value={formData.stoppedMedicationOtherReason || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      stoppedMedicationOtherReason: e.target.value,
                    }))
                  }
                  onBlur={handleAutoSave}
                />
              )}
            </Box>
          )}
        </Box>

        <FormRadioGroup
          label="Har du börjat med någon ny medicin sedan sist?"
          name="hasStartedNewMedication"
          id="q-hasStartedNewMedication"
          value={formData.hasStartedNewMedication}
          options={[
            { value: true, label: 'Ja' },
            { value: false, label: 'Nej' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              hasStartedNewMedication: val,
              newMedicationName: '',
              newMedicationStartDate: '',
              newMedicationReason: '',
            }))
          }
          onBlur={handleAutoSave}
        />
        {formData.hasStartedNewMedication && (
          <>
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="Vilken medicin har du börjat med?"
              value={formData.newMedicationName || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  newMedicationName: e.target.value,
                }))
              }
              onBlur={handleAutoSave}
            />
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="När började du med medicinen?"
              value={formData.newMedicationStartDate || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  newMedicationStartDate: e.target.value,
                }))
              }
              onBlur={handleAutoSave}
            />
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="Av vilket skäl har du börjat med medicinen?"
              value={formData.newMedicationReason || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  newMedicationReason: e.target.value,
                }))
              }
              onBlur={handleAutoSave}
            />
          </>
        )}

        {asksPregnancy && (
          <>
            <FormRadioGroup
              label="Är du gravid?"
              name="pregnant"
              id="q-pregnant"
              value={formData.pregnant}
              options={[
                { label: 'Ja', value: 'yes' },
                { label: 'Nej', value: 'no' },
                { label: 'Vet inte', value: 'unsure' },
              ]}
              onChange={(val) =>
                setFormData((p) => ({
                  ...p,
                  pregnant: val,
                  pregnancyWeek: val === 'yes' ? p.pregnancyWeek : undefined,
                  pregnancyPlan: val === 'no' ? p.pregnancyPlan : undefined,
                }))
              }
              onBlur={handleAutoSave}
            />
            {formData.pregnant === 'yes' && (
              <FormTextInput
                id="q-pregnancyWeek"
                labelFontWeight={400}
                labelFontSize="14px"
                label="Ungefär vilken graviditetsvecka är du i?"
                type="number"
                min={1}
                max={44}
                value={formData.pregnancyWeek?.toString() ?? ''}
                onChange={(e) => {
                  const input = e.target;
                  const { value, validity } = input;
                  if (validity.badInput) {
                    input.value = formData.pregnancyWeek?.toString() ?? '';
                    return;
                  }
                  if (!isValidPregnancyWeek(value)) return;
                  setFormData((p) => ({
                    ...p,
                    pregnancyWeek: value === '' ? undefined : Number(value),
                  }));
                }}
                onBlur={handleAutoSave}
              />
            )}
            {formData.pregnant === 'no' && (
              <FormRadioGroup
                label="Planerar du graviditet inom närmaste 6 månaderna?"
                name="pregnancyPlan"
                id="q-pregnancyPlan"
                value={formData.pregnancyPlan}
                options={[
                  { label: 'Ja', value: 'yes' },
                  { label: 'Nej', value: 'no' },
                ]}
                onChange={(val) =>
                  setFormData((p) => ({ ...p, pregnancyPlan: val }))
                }
                onBlur={handleAutoSave}
              />
            )}
            <FormRadioGroup
              label="Ammar du?"
              name="breastfeeding"
              id="q-breastfeeding"
              value={formData.breastfeeding}
              options={[
                { label: 'Ja', value: 'yes' },
                { label: 'Nej', value: 'no' },
              ]}
              onChange={(val) =>
                setFormData((p) => ({ ...p, breastfeeding: val }))
              }
              onBlur={handleAutoSave}
            />
          </>
        )}

        <FormRadioGroup
          label="Har du drabbats av någon ny sjukdom eller annan medicinsk händelse sedan sist?"
          name="hasNewCondition"
          id="q-hasNewCondition"
          value={formData.hasNewCondition}
          options={[
            { value: true, label: 'Ja' },
            { value: false, label: 'Nej' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              hasNewCondition: val,
              newConditionDescription: '',
            }))
          }
          onBlur={handleAutoSave}
        />
        {formData.hasNewCondition && (
          <FormTextInput
            labelFontWeight={400}
            labelFontSize="14px"
            label="Beskriv den medicinska händelsen/sjukdomen"
            value={formData.newConditionDescription || ''}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                newConditionDescription: e.target.value,
              }))
            }
            onBlur={handleAutoSave}
          />
        )}

        <FormRadioGroup
          label="Är det något annat av vikt som hänt sedan sist, som du tror att läkaren behöver känna till?"
          name="hasOtherImportantInfo"
          id="q-hasOtherImportantInfo"
          value={formData.hasOtherImportantInfo}
          options={[
            { value: true, label: 'Ja' },
            { value: false, label: 'Nej' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              hasOtherImportantInfo: val,
              otherImportantInfo: '',
            }))
          }
          onBlur={handleAutoSave}
        />
        {formData.hasOtherImportantInfo && (
          <FormTextInput
            labelFontWeight={400}
            labelFontSize="14px"
            label="Information av vikt som läkaren behöver känna till"
            value={formData.otherImportantInfo || ''}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                otherImportantInfo: e.target.value,
              }))
            }
            onBlur={handleAutoSave}
          />
        )}

        <FormRadioGroup
          label="Hur många dagar var du frånvarande från arbete eller skola under de senaste 3 månaderna pga din huvudvärk?"
          name="midasWorkDays"
          id="q-midasWorkDays"
          value={formData.midasWorkDays}
          options={midasOptions}
          onChange={(val) => setFormData({ ...formData, midasWorkDays: val })}
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur många dagar de senaste 3 månaderna har du gått till arbetet eller skolan, men utfört dina uppgifter med minst halverad kapacitet?"
          name="midasReducedWorkDays"
          id="q-midasReducedWorkDays"
          value={formData.midasReducedWorkDays}
          options={midasOptions}
          onChange={(val) =>
            setFormData({ ...formData, midasReducedWorkDays: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur många dagar de senaste 3 månaderna, utförde du inte något hushållsarbete pga din huvudvärk?"
          name="midasHouseholdMissed"
          id="q-midasHouseholdMissed"
          value={formData.midasHouseholdMissed}
          options={midasOptions}
          onChange={(val) =>
            setFormData({ ...formData, midasHouseholdMissed: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur många dagar de senaste 3 månaderna utförde du hushållsarbete med minst halverad kapacitet pga din huvudvärk? Räkna inte in de dagar i den föregående frågan då du inte utförde något hushållsarbete alls."
          name="midasHouseholdReduced"
          id="q-midasHouseholdReduced"
          value={formData.midasHouseholdReduced}
          options={midasOptions}
          onChange={(val) =>
            setFormData({ ...formData, midasHouseholdReduced: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur många dagar de senaste 3 månaderna har du pga din huvudvärk avstått från aktiviteter med familjen, vänner eller fritidsaktiviteter?"
          name="midasSocialMissed"
          id="q-midasSocialMissed"
          value={formData.midasSocialMissed}
          options={midasOptions}
          onChange={(val) =>
            setFormData({ ...formData, midasSocialMissed: val })
          }
          onBlur={handleAutoSave}
        />

        <HeadacheDaysQuestion
          value={formData.headacheDaysLast3Months}
          diary={diary}
          onChange={setHeadacheDays}
          onBlur={handleAutoSave}
        />

        <FormTextInput
          id="q-midasPainSeverity"
          label="Ange på en skala 0–10 hur svår i genomsnitt din huvudvärk har varit (0 = ingen smärta, 10 = värsta tänkbara smärta)"
          type="number"
          value={formData.midasPainSeverity?.toString() ?? ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              midasPainSeverity:
                e.target.value === '' ? undefined : +e.target.value,
            })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="När du har huvudvärk, hur ofta är smärtan svår?"
          name="hitPainIntensity"
          id="q-hitPainIntensity"
          value={formData.hitPainIntensity}
          options={hitOptions}
          onChange={(val) =>
            setFormData({ ...formData, hitPainIntensity: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Hur ofta begränsar huvudvärken din förmåga att utföra vanliga vardagsaktiviteter, såsom hushållssysslor, arbete, skola eller sociala aktiviteter?"
          name="hitActivityLimitation"
          id="q-hitActivityLimitation"
          value={formData.hitActivityLimitation}
          options={hitOptions}
          onChange={(val) =>
            setFormData({ ...formData, hitActivityLimitation: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="När du har huvudvärk, hur ofta önskar du att du kunde ligga ner?"
          name="hitDesireToLieDown"
          id="q-hitDesireToLieDown"
          value={formData.hitDesireToLieDown}
          options={hitOptions}
          onChange={(val) =>
            setFormData({ ...formData, hitDesireToLieDown: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Under de senaste 4 veckorna, hur ofta har du känt dig för trött för att arbeta eller utföra dagliga aktiviteter pga din huvudvärk?"
          name="hitFatigueFromHeadache"
          id="q-hitFatigueFromHeadache"
          value={formData.hitFatigueFromHeadache}
          options={hitOptions}
          onChange={(val) =>
            setFormData({ ...formData, hitFatigueFromHeadache: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Under de senaste 4 veckorna, hur ofta har du känt dig less eller irriterad pga din huvudvärk?"
          name="hitIrritabilityFromHeadache"
          id="q-hitIrritabilityFromHeadache"
          value={formData.hitIrritabilityFromHeadache}
          options={hitOptions}
          onChange={(val) =>
            setFormData({ ...formData, hitIrritabilityFromHeadache: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Under de senaste 4 veckorna, hur ofta har din huvudvärk begränsat din förmåga till att koncentrera dig på arbetet eller på dina dagliga aktiviteter?"
          name="hitConcentrationLimitation"
          id="q-hitConcentrationLimitation"
          value={formData.hitConcentrationLimitation}
          options={hitOptions}
          onChange={(val) =>
            setFormData({ ...formData, hitConcentrationLimitation: val })
          }
          onBlur={handleAutoSave}
        />

        <FormRadioGroup
          label="Har du blivit ombedd att mäta ditt blodtryck?"
          name="bloodPressureInstruction"
          id="q-bloodPressureInstruction"
          value={formData.bloodPressureInstruction}
          options={[
            { value: 'yes_measured', label: 'Ja' },
            {
              value: 'yes_not_measured',
              label: 'Ja, men jag har inte mätt det',
            },
            { value: 'no', label: 'Nej' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              bloodPressureInstruction: val,
            }))
          }
          onBlur={handleAutoSave}
        />
        {formData.bloodPressureInstruction === 'yes_measured' && (
          <>
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="Systoliskt"
              value={formData.systolicPressure || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  systolicPressure: e.target.value,
                }))
              }
              onBlur={handleAutoSave}
            />
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="Diastoliskt"
              value={formData.diastolicPressure || ''}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  diastolicPressure: e.target.value,
                }))
              }
              onBlur={handleAutoSave}
            />
            <FormTextInput
              labelFontWeight={400}
              labelFontSize="14px"
              label="Puls"
              value={formData.pulse || ''}
              onChange={(e) =>
                setFormData((p) => ({ ...p, pulse: e.target.value }))
              }
              onBlur={handleAutoSave}
            />
          </>
        )}

        <FormRadioGroup
          label="Behöver du förnya recept på något läkemedel du använder för din huvudvärk?"
          name="needsPrescriptionRenewal"
          id="q-needsPrescriptionRenewal"
          value={formData.needsPrescriptionRenewal}
          options={[
            { value: true, label: 'Ja' },
            { value: false, label: 'Nej' },
          ]}
          onChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              needsPrescriptionRenewal: val,
              prescriptionDetails: '',
            }))
          }
          onBlur={handleAutoSave}
        />
        {formData.needsPrescriptionRenewal && (
          <FormTextInput
            labelFontWeight={400}
            labelFontSize="14px"
            label="Vilket/Vilka läkemdel behöver du receptförnyelse på? (Läkemedel och dos)"
            value={formData.prescriptionDetails || ''}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                prescriptionDetails: e.target.value,
              }))
            }
            onBlur={handleAutoSave}
          />
        )}

        {showUnanswered && (
          <UnansweredQuestions ref={incompleteRef} unanswered={unanswered} />
        )}

        <Box
          role="group"
          aria-labelledby="confirm-submit-title"
          sx={{
            mx: -3,
            borderTop: '1px solid #FECACA',
            borderBottom: '1px solid #FECACA',
            backgroundColor: '#FEF2F2',
            pl: 3,
            pr: 4,
            py: 1.5,
            color: '#111827',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                sx={{
                  color: '#D1D5DB',
                  '&.Mui-checked': { color: DS.deepTeal },
                  mt: -0.5,
                  alignSelf: 'flex-start',
                }}
              />
            }
            label="Jag har besvarat alla frågor och är medveten om att jag inte kan ändra eller lägga till något när jag skickat in."
            sx={{
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                fontSize: '16px',
                lineHeight: 1.6,
                fontFamily: DS.fontBody,
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          name="SaveForm"
          disabled={submitDisabled}
          aria-describedby={
            showUnanswered ? UNANSWERED_QUESTIONS_ID : undefined
          }
          fullWidth
          sx={{
            mt: 3,
            py: 1.5,
            fontSize: '16px',
            fontWeight: 400,
            fontFamily: DS.fontBody,
            borderRadius: '4px',
            textTransform: 'uppercase',
            color: '#fff',
            backgroundColor: submitDisabled ? '#D1D5DB' : DS.deepTeal,
            '&:hover': {
              backgroundColor: submitDisabled ? '#D1D5DB' : '#0a5854',
            },
            '&.Mui-disabled': {
              color: '#fff',
              backgroundColor: '#D1D5DB',
            },
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: '#fff' }} />
              SKICKAR IN...
            </Box>
          ) : (
            'SKICKA IN FORMULÄR'
          )}
        </Button>

        {error && (
          <Typography
            ref={errorRef}
            sx={{
              mt: 1,
              color: 'error.main',
              fontSize: '14px',
              fontFamily: DS.fontBody,
            }}
          >
            {error}
          </Typography>
        )}
      </FormLayout>
    </form>
  );
};

export default FollowUpForm;
